import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { useClassFilter } from '@/contexts/ClassFilterContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Plus, DollarSign, Clock, Search, ArrowLeft, AlertTriangle, CheckCircle, XCircle,
  FileText, Users, MessageSquare, RefreshCw, Shield, Loader2, ChevronRight, Send, Sparkles
} from 'lucide-react';
import EmptyState from '@/components/EmptyState';
import RecordDocuments from '@/components/RecordDocuments';
import { toast } from 'sonner';
import { ClaimTimeline } from '@/components/ClaimTimeline';
import StructuredDeclineForm from '@/components/StructuredDeclineForm';
import PartFPromisesCard from '@/components/PartFPromisesCard';
import StaleClaimsCard, { IdleChip } from '@/components/StaleClaimsCard';
import QuantumBacklogCard from '@/components/QuantumBacklogCard';

function PaymentRunButton({ api }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [runs, setRuns] = useState(null);

  useEffect(() => {
    api.get('/claims/payment-run/preview').then(r => setPreview(r.data)).catch(() => {});
  }, [api]);

  const openHistory = async () => {
    if (!showHistory) {
      try { const r = await api.get('/claims/payment-run/history'); setRuns(r.data.runs); } catch (e) { setRuns([]); }
    }
    setShowHistory(v => !v);
  };

  const downloadCsv = async (runId) => {
    try {
      const res = await api.get(`/claims/payment-run/${runId}/csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `claim-payment-run-${runId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not rebuild that run — no claims found for it'); }
  };

  const exportRun = async () => {
    setBusy(true);
    try {
      const res = await api.post('/claims/payment-run/export', {}, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `claim-payment-run-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Payment instructions exported for ${preview?.count} settlement${preview?.count === 1 ? '' : 's'} — claims marked as exported`);
      setPreview({ count: 0, total: 0, claims: [] });
    } catch (e) {
      toast.error('No settled claims awaiting payment export');
    } finally { setBusy(false); }
  };

  if (!preview) return null;
  const heldChip = preview.held > 0 && (
    <span data-testid="payment-run-held-chip"
      title={`${preview.held} settled claim(s) totalling $${(preview.held_total || 0).toLocaleString()} held from the payment run until the signed discharge receipt is marked returned`}
      className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold whitespace-nowrap"
      style={{ background: 'rgba(232,160,32,0.12)', color: '#B27516', border: '1px solid rgba(232,160,32,0.35)' }}>
      <Clock className="w-3.5 h-3.5" />
      {preview.held} held on discharge
    </span>
  );
  return (
    <div className="relative flex items-center gap-2">
      {heldChip}
      {preview && preview.count > 0 && (
        <button onClick={exportRun} disabled={busy} data-testid="payment-run-btn"
          title={`Export a bank payment-instruction CSV for ${preview.count} settled claim(s) totalling $${(preview.total || 0).toLocaleString()}${preview.held > 0 ? ` — ${preview.held} more held awaiting signed discharge` : ''}`}
          className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold text-white whitespace-nowrap disabled:opacity-50"
          style={{ background: C.green }}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DollarSign className="w-3.5 h-3.5" />}
          Payment Run ({preview.count} · ${(preview.total || 0) >= 1000 ? `${Math.round(preview.total / 1000)}K` : preview.total})
        </button>
      )}
      <button onClick={openHistory} data-testid="payment-run-history-btn"
        title="Past payment runs — re-download any batch CSV for reconciliation"
        className="flex items-center gap-1.5 px-2.5 py-2 rounded-md text-xs font-semibold whitespace-nowrap"
        style={{ background: showHistory ? '#0D1B2A' : 'rgba(13,27,42,0.06)', color: showHistory ? '#fff' : '#0D1B2A', border: '1px solid rgba(136,150,168,0.25)' }}>
        <RefreshCw className="w-3.5 h-3.5" />
        Runs
      </button>
      {showHistory && (
        <div data-testid="payment-run-history-panel"
          className="absolute right-0 top-full mt-2 z-50 w-[720px] max-h-[360px] overflow-y-auto rounded-md bg-white shadow-lg"
          style={{ border: '1px solid rgba(136,150,168,0.25)' }}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
            <span className="text-xs font-semibold" style={{ color: '#0D1B2A' }}>Payment Run History</span>
            <button onClick={() => setShowHistory(false)} data-testid="payment-run-history-close" className="text-xs" style={{ color: '#5C6B7A' }}>Close</button>
          </div>
          {!runs ? (
            <div className="p-4 text-xs" style={{ color: '#5C6B7A' }}>Loading…</div>
          ) : runs.length === 0 ? (
            <div className="p-4 text-xs" style={{ color: '#5C6B7A' }}>No payment runs exported yet.</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b" style={{ background: '#FAFBFC', borderColor: 'rgba(136,150,168,0.1)' }}>
                  {['Run', 'Date', 'Claims', 'Total', 'By', ''].map(h => (
                    <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: '#5C6B7A' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.run_id} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`payment-run-row-${r.run_id}`}>
                    <td className="py-2 px-3 font-mono font-semibold" style={{ color: '#0D1B2A' }}>{r.run_id}</td>
                    <td className="py-2 px-3" style={{ color: '#5C6B7A' }}>{(r.at || '').slice(0, 10)}</td>
                    <td className="py-2 px-3" style={{ color: '#0D1B2A' }}>{r.claims}</td>
                    <td className="py-2 px-3 font-semibold" style={{ color: C.green }}>${(r.total || 0).toLocaleString()}</td>
                    <td className="py-2 px-3" style={{ color: '#5C6B7A' }}><div className="truncate w-[120px]">{r.by}</div></td>
                    <td className="py-2 px-3 text-right">
                      <button onClick={() => downloadCsv(r.run_id)} data-testid={`payment-run-download-${r.run_id}`}
                        title="Re-download this run's CSV"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold"
                        style={{ background: 'rgba(29,158,117,0.1)', color: C.green }}>
                        <FileText className="w-3 h-3" /> CSV
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
import { ClaimReinsurancePanel } from '@/pages/ReinsurancePage';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA' };

const STATUS_CONFIG = {
  fnol_received: { color: C.teal, label: 'FNOL' }, under_review: { color: C.blue, label: 'Review' },
  info_requested: { color: C.amber, label: 'Info Requested' }, assessor_appointed: { color: C.blue, label: 'Assessor' },
  assessment_complete: { color: C.teal, label: 'Assessed' }, settlement_pending: { color: C.amber, label: 'Pending' },
  settlement_approved: { color: C.green, label: 'Approved' }, settled: { color: C.green, label: 'Settled' },
  partially_settled: { color: C.amber, label: 'Partial' }, declined: { color: C.red, label: 'Declined' },
  withdrawn: { color: C.slate, label: 'Withdrawn' }, recovery_in_progress: { color: C.blue, label: 'Recovery' },
  closed: { color: C.slate, label: 'Closed' }, closed_no_payment: { color: C.slate, label: 'Closed — no payment' },
  reopened: { color: C.amber, label: 'Reopened' },
};

// A status the engine has not declared must read as itself, never as another status. Every open
// file used to render as "FNOL" because the fallback pointed at fnol_received.
const statusMeta = (status) => STATUS_CONFIG[status]
  || { color: C.slate, label: String(status || 'unknown').replace(/_/g, ' ') };

const PATHWAY_CONFIG = {
  fast_track: { color: C.green, label: 'Fast Track' }, standard: { color: C.blue, label: 'Standard' },
  complex: { color: C.amber, label: 'Complex' }, siu: { color: C.red, label: 'SIU' },
  decline_to_investigate: { color: C.red, label: 'Decline' }, cat_fast_track: { color: C.red, label: 'CAT Fast Track' },
};

function fmt$(v) { if (!v) return '$0'; if (v >= 1e6) return `$${(v/1e6).toFixed(1)}M`; if (v >= 1e3) return `$${(v/1e3).toFixed(0)}K`; return `$${Math.round(v).toLocaleString()}`; }

const SIU_REASONS = [
  { code: 'LATE_REPORT', label: 'Late reporting of loss (>30 days)' },
  { code: 'RECENT_INCEPTION', label: 'Loss shortly after policy inception' },
  { code: 'INCONSISTENT_STATEMENTS', label: 'Inconsistent or changing statements' },
  { code: 'EXCESSIVE_QUANTUM', label: 'Quantum excessive for loss described' },
  { code: 'DOC_ANOMALY', label: 'Documentation anomaly or suspected alteration' },
  { code: 'STAGED_LOSS', label: 'Indicators of staged or fabricated loss' },
  { code: 'PRIOR_FRAUD', label: 'Prior fraud indicator on insured or claimant' },
  { code: 'FINANCIAL_DISTRESS', label: 'Insured in financial distress' },
  { code: 'REPEAT_CLAIMANT', label: 'Repeat claimant / claims frequency pattern' },
  { code: 'TIP_OFF', label: 'Third-party tip-off or allegation' },
];
const SIU_STATUS_META = {
  open: { label: 'Open', color: C.red }, investigating: { label: 'Investigating', color: C.amber },
  cleared: { label: 'Cleared', color: C.green }, substantiated: { label: 'Substantiated', color: '#7C2D12' },
  withdrawn: { label: 'Withdrawn', color: C.slate },
};

export default function ClaimsPage() {
  const api = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { classFilter } = useClassFilter();
  const [claims, setClaims] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ policy_number: '', business_name: '', claim_type: 'property', description: '', estimated_quantum: '', claimant_name: '', claimant_email: '', date_of_loss: '', location_of_loss: '' });
  const [formErr, setFormErr] = useState('');
  const [mismatchQ, setMismatchQ] = useState(null);
  const [showQueue, setShowQueue] = useState(false);
  const [diaryQ, setDiaryQ] = useState(null);
  const [showDiaryQ, setShowDiaryQ] = useState(false);
  const [siuQ, setSiuQ] = useState(null);
  const [showSiuQ, setShowSiuQ] = useState(false);
  const [fraudQ, setFraudQ] = useState(null);
  const [showFraudQ, setShowFraudQ] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [dismissing, setDismissing] = useState(null);
  const [dismissReason, setDismissReason] = useState('');
  const [resolving, setResolving] = useState(null);
  const [resolveErr, setResolveErr] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  // "isAdmin" here means "may work claims" — the claims handler works files too (backend _HANDLER guard); staff-only panels use isStaff
  const isStaff = ['platform_owner', 'mga_admin', 'underwriter'].includes(user?.role);
  const isAdmin = isStaff || user?.role === 'claims_handler';
  const isLead = isStaff || !!user?.claims_lead;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    let params = classFilter && classFilter !== 'all' ? `?limit=100&insurance_class=${classFilter}` : '?limit=100';
    if (search.trim()) params += `&search=${encodeURIComponent(search.trim())}`;
    if (statusFilter !== 'all') params += `&status=${statusFilter}`;
    Promise.all([
      api.get('/claims' + params),
      isAdmin ? api.get('/claims/dashboard') : Promise.resolve({ data: null }),
      isAdmin ? api.get('/claims/mismatch-queue') : Promise.resolve({ data: null }),
      isAdmin ? api.get('/claims/diary/queue') : Promise.resolve({ data: null }),
      isAdmin ? api.get('/claims/siu/queue') : Promise.resolve({ data: null }),
      isAdmin ? api.get('/claims/fraud-scan/suggestions') : Promise.resolve({ data: null }),
    ]).then(([c, d, m, dq, sq, fq]) => { setClaims(c.data); setDashboard(d.data); setMismatchQ(m.data); setDiaryQ(dq.data); setSiuQ(sq.data); setFraudQ(fq.data); setLastRefresh(new Date()); }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api, classFilter, isAdmin, search, statusFilter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, search ? 400 : 0); return () => clearTimeout(t); }, [load]);

  // Live book — auto-refresh every 30s so settled claims leave the open view immediately
  useEffect(() => {
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible' && !selectedClaim && !showForm) load();
    }, 30000);
    return () => clearInterval(iv);
  }, [load, selectedClaim, showForm]);

  const filtered = useMemo(() => {
    let result = claims;
    if (search) { const q = search.toLowerCase(); result = result.filter(c => c.claim_reference?.toLowerCase().includes(q) || c.business_name?.toLowerCase().includes(q)); }
    return result;
  }, [claims, search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormErr('');
    try {
      await api.post('/claims', { ...form, estimated_quantum: parseFloat(form.estimated_quantum) || 0 });
      setShowForm(false);
      setForm({ policy_number: '', business_name: '', claim_type: 'property', description: '', estimated_quantum: '', claimant_name: '', claimant_email: '', date_of_loss: '', location_of_loss: '' });
      load();
    } catch (err) { setFormErr(err?.response?.data?.detail || 'Failed to lodge FNOL'); }
    setSubmitting(false);
  };

  const openFromQueue = async (claimId) => {
    try { const { data } = await api.get(`/claims/${claimId}`); setSelectedClaim(data); }
    catch (err) { console.error('API error:', err?.message); }
  };

  // Deep link: /claims?claim={id} (digest emails) or /claims/{id} (direct URL)
  useEffect(() => {
    const qId = new URLSearchParams(window.location.search).get('claim');
    const pathMatch = window.location.pathname.match(/^\/claims\/([0-9a-f-]{8,})/i);
    const id = qId || (pathMatch && pathMatch[1]);
    if (id) openFromQueue(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitResolve = async () => {
    if (!resolving) return;
    setResolveErr('');
    try {
      await api.post(`/claims/${resolving.claimId}/resolve-mismatch`, {
        action: resolving.mode,
        new_quantum: resolving.mode === 'adjust' ? parseFloat(resolving.amount) : null,
        reason: resolving.reason,
      });
      setResolving(null);
      load();
    } catch (err) { setResolveErr(err?.response?.data?.detail || 'Failed to resolve'); }
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;

  // ─── Claim Detail View ───
  if (selectedClaim) return <ClaimDetail claim={selectedClaim} api={api} isAdmin={isAdmin} isStaff={isStaff} onBack={() => {
    if (location.state?.from === 'finance') { navigate('/finance?tab=claimpayments'); return; }
    setSelectedClaim(null); load();
  }} onChanged={load} user={user} />;

  return (
    <div className="space-y-4" data-testid="claims-page">
      {/* Dashboard KPIs */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI label="Open" value={dashboard.open} color={C.amber} />
          <KPI label="Settled" value={dashboard.settled?.toLocaleString()} color={C.green} />
          <KPI label="Declined" value={dashboard.declined} color={C.red} />
          <KPI label="Reserves" value={fmt$(dashboard.open_reserves)} color={C.amber} />
          <KPI label="Total Paid" value={fmt$(dashboard.total_paid)} color={C.blue} />
        </div>
      )}
      {dashboard && (
        <p className="text-[10px] -mt-1" style={{ color: '#64748B' }}>Amber reserve = open exposure · settled claims release reserves to $0</p>
      )}

      {/* ARIA stale files — chased at 60 d, escalated at 74 d, so IN-26 never has to fire at 90 */}
      {isLead && <StaleClaimsCard api={api} onOpen={(id) => { const c = claims.find(x => x.claim_id === id); if (c) setSelectedClaim(c); else api.get(`/claims/${id}`).then(r => setSelectedClaim(r.data)).catch(() => {}); }} />}

      {/* IN-06 — assessed files with no stated quantum; the claims team records the figure from the card */}
      {isAdmin && <QuantumBacklogCard api={api} onOpen={(id) => api.get(`/claims/${id}`).then(r => setSelectedClaim(r.data)).catch(() => {})} />}

      {/* ARIA Exec Exceptions Queue */}
      {isAdmin && diaryQ && (
        <Card className="rounded-md" style={{ border: `1px solid ${diaryQ.open_escalations > 0 ? 'rgba(226,75,74,0.3)' : 'rgba(29,158,117,0.3)'}` }} data-testid="diary-queue">
          <button onClick={() => setShowDiaryQ(!showDiaryQ)} className="w-full flex items-center justify-between px-4 py-3" data-testid="diary-queue-toggle">
            <div className="flex items-center gap-2 flex-wrap">
              {diaryQ.open_escalations > 0
                ? <AlertTriangle className="w-4 h-4" style={{ color: C.red }} />
                : <CheckCircle className="w-4 h-4" style={{ color: C.green }} />}
              <span className="text-sm font-semibold" style={{ color: C.navy }}>ARIA Claims — C-suite Decisions</span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                style={{ background: (diaryQ.open_escalations > 0 ? C.red : C.green) + '18', color: diaryQ.open_escalations > 0 ? C.red : C.green }}
                data-testid="diary-queue-count">{diaryQ.open_escalations} need a decision</span>
              {diaryQ.aria_last_run?.aria_actions != null && (
                <span className="text-[11px] hidden md:inline" style={{ color: C.slate }} data-testid="aria-autonomy-stat">
                  ARIA autonomously handled {diaryQ.aria_last_run.aria_actions?.toLocaleString()} diary action(s) across {diaryQ.aria_last_run.claims_worked?.toLocaleString()} claims
                </span>
              )}
              {Object.entries(diaryQ.by_trigger || {}).map(([code, n]) => (
                <span key={code} className="px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ background: 'rgba(226,75,74,0.08)', color: C.red }} data-testid={`diary-trigger-${code}`}>{code.replace(/_/g, ' ')}: {n}</span>
              ))}
            </div>
            <span className="text-xs" style={{ color: C.blue }}>{showDiaryQ ? 'Hide' : 'Review'}</span>
          </button>
          {showDiaryQ && (
            <div className="overflow-x-auto border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
              {diaryQ.items.length === 0 ? (
                <p className="px-4 py-4 text-xs" style={{ color: C.slate }} data-testid="diary-queue-empty">
                  Nothing needs a human — ARIA is handling the entire open book autonomously. Exceptions (material quantum, coverage disputes, beyond-authority reserves, fraud/SIU) will appear here.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: C.bg }}>
                      {['Reference', 'Insured', 'Quantum', 'Why ARIA escalated', 'ARIA recommends', ''].map(h => (
                        <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {diaryQ.items.map(it => (
                      <tr key={it.escalation_id} className="border-b hover:bg-[#F5F7FA]" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`diary-row-${it.claim_id}`}>
                        <td className="py-2 px-3 font-mono text-xs" style={{ color: C.blue }}>{it.claim_reference}</td>
                        <td className="py-2 px-3 text-xs truncate max-w-[150px]" style={{ color: C.navy }}>{it.business_name}</td>
                        <td className="py-2 px-3 text-xs font-mono font-semibold" style={{ color: C.navy }}>{fmt$(it.estimated_quantum)}</td>
                        <td className="py-2 px-3">
                          <div className="flex flex-wrap gap-1 max-w-[260px]">
                            {(it.triggers || []).map(t => (
                              <span key={t.code} title={t.detail} className="px-1 py-0.5 rounded text-[8px] font-bold cursor-help" style={{ background: 'rgba(226,75,74,0.1)', color: C.red }}>{t.code.replace(/_/g, ' ')}</span>
                            ))}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-[11px]" style={{ color: C.slate, maxWidth: '220px' }}><span className="line-clamp-2">{it.recommendation}</span></td>
                        <td className="py-2 px-3 whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1.5">
                            <button onClick={() => openFromQueue(it.claim_id)} className="px-2 py-1 rounded-md text-[10px] font-semibold" style={{ background: 'rgba(26,76,124,0.08)', color: C.blue }} data-testid={`diary-review-${it.claim_id}`}>Review</button>
                            <button data-testid={`diary-resolve-${it.escalation_id}`}
                              onClick={async () => { try { await api.post(`/claims/diary/escalations/${it.escalation_id}/resolve`, { note: 'Cleared for autonomous handling' }); load(); } catch (e) { console.error('API error:', e?.message); } }}
                              className="px-2 py-1 rounded-md text-[10px] font-semibold text-white" style={{ background: C.green }}>Clear</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </Card>
      )}

      {/* ARIA Fraud Pattern Suggestions */}
      {isAdmin && fraudQ && (
        <Card className="rounded-md" style={{ border: '1px solid rgba(0,180,216,0.35)' }} data-testid="fraud-scan-card">
          <div className="w-full flex items-center justify-between px-4 py-3 flex-wrap gap-2">
            <button onClick={() => setShowFraudQ(!showFraudQ)} className="flex items-center gap-2 flex-wrap" data-testid="fraud-scan-toggle">
              <AlertTriangle className="w-4 h-4" style={{ color: C.teal }} />
              <span className="text-sm font-semibold" style={{ color: C.navy }}>ARIA Fraud Patterns</span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(0,180,216,0.12)', color: '#0E7490' }} data-testid="fraud-scan-count">{fraudQ.suggested} suggested</span>
              {fraudQ.last_run && <span className="text-[11px] hidden md:inline" style={{ color: C.slate }}>Last scan {new Date(fraudQ.last_run.at).toLocaleString('en-AU')} · {fraudQ.last_run.claims_scanned} claims scanned</span>}
            </button>
            <div className="flex items-center gap-2">
              <button disabled={scanBusy} data-testid="fraud-scan-run-btn"
                onClick={async () => { setScanBusy(true); try { await api.post('/claims/fraud-scan/run'); load(); } catch (e) { console.error('API error:', e?.message); } setScanBusy(false); }}
                className="px-2.5 py-1 rounded-md text-[10px] font-semibold text-white disabled:opacity-50" style={{ background: C.teal }}>
                {scanBusy ? 'Scanning…' : 'Run Scan Now'}
              </button>
              <button onClick={() => setShowFraudQ(!showFraudQ)} className="text-xs" style={{ color: C.blue }}>{showFraudQ ? 'Hide' : 'Triage'}</button>
            </div>
          </div>
          {showFraudQ && (
            <div className="overflow-x-auto border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
              {fraudQ.items.length === 0 ? (
                <p className="px-4 py-4 text-xs" style={{ color: C.slate }} data-testid="fraud-scan-empty">No pattern suggestions outstanding — the book is clean or everything has been triaged.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: C.bg }}>
                      {['Reference', 'Business', 'Quantum', 'Patterns Detected', 'Score', ''].map(h => (
                        <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fraudQ.items.map(s => (
                      <React.Fragment key={s.suggestion_id}>
                      <tr className="border-b hover:bg-[#F5F7FA]" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`fraud-row-${s.suggestion_id}`}>
                        <td className="py-2 px-3 font-mono text-xs" style={{ color: C.blue }}>{s.claim_reference}</td>
                        <td className="py-2 px-3 text-xs truncate max-w-[140px]" style={{ color: C.navy }}>{s.business_name}</td>
                        <td className="py-2 px-3 text-xs font-mono" style={{ color: C.navy }}>{fmt$(s.estimated_quantum)}</td>
                        <td className="py-2 px-3">
                          <div className="flex flex-wrap gap-1 max-w-[340px]">
                            {(s.patterns || []).map(p => (
                              <span key={p.code} title={p.detail} className="px-1 py-0.5 rounded text-[8px] font-bold cursor-help" style={{ background: 'rgba(0,180,216,0.1)', color: '#0E7490' }}>{p.code}</span>
                            ))}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-xs font-mono font-bold" style={{ color: s.score >= 3 ? C.red : '#B27516' }}>{s.score}</td>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-1.5">
                            <button data-testid={`fraud-refer-${s.suggestion_id}`}
                              onClick={async () => { try { await api.post(`/claims/fraud-scan/suggestions/${s.suggestion_id}/refer`); load(); } catch (e) { console.error('API error:', e?.message); } }}
                              className="px-2 py-1 rounded-md text-[10px] font-semibold text-white" style={{ background: '#7C2D12' }}>Refer to SIU</button>
                            <button onClick={() => { setDismissing(dismissing === s.suggestion_id ? null : s.suggestion_id); setDismissReason(''); }} data-testid={`fraud-dismiss-${s.suggestion_id}`}
                              className="px-2 py-1 rounded-md text-[10px] font-semibold" style={{ background: 'rgba(92,107,122,0.1)', color: C.slate }}>Dismiss</button>
                            <button onClick={() => openFromQueue(s.claim_id)} className="px-2 py-1 rounded-md text-[10px] font-semibold" style={{ background: 'rgba(26,76,124,0.08)', color: C.blue }} data-testid={`fraud-review-${s.suggestion_id}`}>Review</button>
                          </div>
                        </td>
                      </tr>
                      {dismissing === s.suggestion_id && (
                        <tr style={{ background: C.bg }}>
                          <td colSpan={6} className="py-2 px-3">
                            <div className="flex items-center gap-2">
                              <Input value={dismissReason} onChange={e => setDismissReason(e.target.value)} placeholder="Reason for dismissing this pattern suggestion (required)" className="text-xs rounded-md max-w-md" data-testid="fraud-dismiss-reason" />
                              <Button size="sm" disabled={!dismissReason.trim()} data-testid="fraud-dismiss-confirm"
                                onClick={async () => { try { await api.post(`/claims/fraud-scan/suggestions/${s.suggestion_id}/dismiss`, { reason: dismissReason }); setDismissing(null); load(); } catch (e) { console.error('API error:', e?.message); } }}
                                className="text-xs rounded-md" style={{ background: C.slate }}>Confirm</Button>
                              <Button size="sm" variant="outline" onClick={() => setDismissing(null)} className="text-xs rounded-md">Cancel</Button>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </Card>
      )}

      {/* SIU Referral Queue */}
      {isAdmin && siuQ && siuQ.total > 0 && (
        <Card className="rounded-md" style={{ border: '1px solid rgba(124,45,18,0.35)' }} data-testid="siu-queue">
          <button onClick={() => setShowSiuQ(!showSiuQ)} className="w-full flex items-center justify-between px-4 py-3" data-testid="siu-queue-toggle">
            <div className="flex items-center gap-2 flex-wrap">
              <Shield className="w-4 h-4" style={{ color: '#7C2D12' }} />
              <span className="text-sm font-semibold" style={{ color: C.navy }}>SIU Referrals</span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(124,45,18,0.1)', color: '#7C2D12' }} data-testid="siu-queue-count">{siuQ.active} active</span>
              {Object.entries(siuQ.by_status || {}).map(([st, n]) => (
                <span key={st} className="px-1.5 py-0.5 rounded text-[9px] font-semibold capitalize" style={{ background: (SIU_STATUS_META[st]?.color || C.slate) + '12', color: SIU_STATUS_META[st]?.color || C.slate }} data-testid={`siu-status-${st}`}>{st}: {n}</span>
              ))}
            </div>
            <span className="text-xs" style={{ color: C.blue }}>{showSiuQ ? 'Hide' : 'Triage'}</span>
          </button>
          {showSiuQ && (
            <div className="overflow-x-auto border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: C.bg }}>
                    {['Reference', 'Business', 'Reason Codes', 'Status', 'Investigator', 'Referred By', 'Opened', ''].map(h => (
                      <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {siuQ.items.map(r => {
                    const sm = SIU_STATUS_META[r.status] || SIU_STATUS_META.open;
                    return (
                      <tr key={r.referral_id} className="border-b hover:bg-[#F5F7FA]" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`siu-row-${r.referral_id}`}>
                        <td className="py-2 px-3 font-mono text-xs" style={{ color: C.blue }}>{r.claim_reference}</td>
                        <td className="py-2 px-3 text-xs truncate max-w-[150px]" style={{ color: C.navy }}>{r.business_name}</td>
                        <td className="py-2 px-3">
                          <div className="flex flex-wrap gap-1 max-w-[220px]">
                            {(r.reason_codes || []).map(c => (
                              <span key={c} className="px-1 py-0.5 rounded text-[8px] font-bold" style={{ background: 'rgba(124,45,18,0.08)', color: '#7C2D12' }}>{c}</span>
                            ))}
                          </div>
                        </td>
                        <td className="py-2 px-3"><span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: sm.color + '15', color: sm.color }}>{sm.label}</span></td>
                        <td className="py-2 px-3 text-xs" style={{ color: C.slate }}>{r.investigator || '—'}</td>
                        <td className="py-2 px-3 text-xs" style={{ color: C.slate }}>{r.referred_by}</td>
                        <td className="py-2 px-3 text-xs" style={{ color: C.slate }}>{new Date(r.created_at).toLocaleDateString('en-AU')}</td>
                        <td className="py-2 px-3">
                          <button onClick={() => openFromQueue(r.claim_id)} className="px-2 py-1 rounded-md text-[10px] font-semibold" style={{ background: 'rgba(26,76,124,0.08)', color: C.blue }} data-testid={`siu-review-${r.referral_id}`}>Review</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Quantum Mismatch Work Queue */}
      {isAdmin && mismatchQ && mismatchQ.count > 0 && (
        <Card className="rounded-md" style={{ border: '1px solid rgba(232,160,32,0.35)' }} data-testid="mismatch-queue">
          <button onClick={() => setShowQueue(!showQueue)} className="w-full flex items-center justify-between px-4 py-3" data-testid="mismatch-queue-toggle">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" style={{ color: C.amber }} />
              <span className="text-sm font-semibold" style={{ color: C.navy }}>Quantum Mismatch Queue</span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(232,160,32,0.15)', color: '#B27516' }} data-testid="mismatch-count">{mismatchQ.count}</span>
              <span className="text-[11px] hidden md:inline" style={{ color: C.slate }}>ARIA auto-resolves mismatches — items here were escalated for embedded human judgement</span>
            </div>
            <span className="text-xs" style={{ color: C.blue }}>{showQueue ? 'Hide' : 'Triage'}</span>
          </button>
          {showQueue && (
            <div className="overflow-x-auto border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: C.bg }}>
                    {['Reference', 'Business', 'Claim Quantum', 'Document Total', 'Variance', 'Document', 'Flagged', ''].map(h => (
                      <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mismatchQ.claims.map(c => {
                    const m = c.quantum_mismatch || {};
                    const over = m.status === 'over_quantum';
                    return (
                      <React.Fragment key={c.claim_id}>
                      <tr className="border-b hover:bg-[#F5F7FA]" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`mismatch-row-${c.claim_id}`}>
                        <td className="py-2 px-3 font-mono text-xs" style={{ color: C.blue }}>{c.claim_reference}{c.simulation && <span className="ml-1 px-1 rounded text-[8px] font-bold" style={{ background: 'rgba(0,180,216,0.12)', color: '#0E7490' }}>SIM</span>}</td>
                        <td className="py-2 px-3 text-xs truncate max-w-[160px]" style={{ color: C.navy }}>{c.business_name}</td>
                        <td className="py-2 px-3 text-xs font-mono" style={{ color: C.navy }}>{fmt$(m.claim_quantum || c.estimated_quantum)}</td>
                        <td className="py-2 px-3 text-xs font-mono font-medium" style={{ color: over ? C.red : '#B27516' }}>{fmt$(m.document_total)}</td>
                        <td className="py-2 px-3">
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold font-mono" style={{ background: over ? 'rgba(226,75,74,0.1)' : 'rgba(232,160,32,0.12)', color: over ? C.red : '#B27516' }}>
                            {m.variance_pct > 0 ? '+' : ''}{m.variance_pct}%
                          </span>
                        </td>
                        <td className="py-2 px-3 text-[11px] truncate max-w-[140px]" style={{ color: C.slate }}>{m.document}</td>
                        <td className="py-2 px-3 text-[11px]" style={{ color: C.slate }}>{m.checked_at ? new Date(m.checked_at).toLocaleDateString('en-AU') : '—'}</td>
                        <td className="py-2 px-3">
                          <div className="flex gap-1.5">
                            <button onClick={() => openFromQueue(c.claim_id)} className="px-2 py-1 rounded text-[10px] font-medium text-white" style={{ background: C.blue }} data-testid={`mismatch-open-${c.claim_id}`}>Review</button>
                            <button onClick={() => { setResolveErr(''); setResolving({ claimId: c.claim_id, mode: 'adjust', amount: String(m.document_total || ''), reason: '' }); }}
                              className="px-2 py-1 rounded text-[10px] font-medium" style={{ background: 'rgba(29,158,117,0.1)', color: C.green }} data-testid={`mismatch-adjust-${c.claim_id}`}>Adjust</button>
                            <button onClick={() => { setResolveErr(''); setResolving({ claimId: c.claim_id, mode: 'dismiss', amount: '', reason: '' }); }}
                              className="px-2 py-1 rounded text-[10px] font-medium" style={{ background: 'rgba(136,150,168,0.12)', color: C.slate }} data-testid={`mismatch-dismiss-${c.claim_id}`}>Dismiss</button>
                          </div>
                        </td>
                      </tr>
                      {resolving?.claimId === c.claim_id && (
                        <tr><td colSpan={8} className="px-4 py-3" style={{ background: '#FFF9EF' }}>
                          <div className="flex flex-wrap items-center gap-2" data-testid="mismatch-resolve-form">
                            {resolving.mode === 'adjust' ? (<>
                              <span className="text-[11px] font-medium" style={{ color: C.navy }}>Adjust claim quantum to</span>
                              <Input type="number" value={resolving.amount} onChange={e => setResolving({ ...resolving, amount: e.target.value })}
                                className="w-32 h-7 text-xs rounded-md" data-testid="resolve-amount-input" />
                              <span className="text-[10px]" style={{ color: C.slate }}>(document total: {fmt$(m.document_total)} · reserve recalculates net of excess)</span>
                            </>) : (
                              <span className="text-[11px] font-medium" style={{ color: C.navy }}>Dismiss mismatch — reason required</span>
                            )}
                            <Input value={resolving.reason} onChange={e => setResolving({ ...resolving, reason: e.target.value })}
                              placeholder={resolving.mode === 'adjust' ? 'Note (optional)' : 'e.g. Document covers multiple claims / quote superseded'}
                              className="flex-1 min-w-[200px] h-7 text-xs rounded-md" data-testid="resolve-reason-input" />
                            <Button size="sm" onClick={submitResolve} className="h-7 text-xs rounded-md" style={{ background: resolving.mode === 'adjust' ? C.green : C.slate }}
                              data-testid="resolve-submit-btn">{resolving.mode === 'adjust' ? 'Apply Adjustment' : 'Dismiss'}</Button>
                            <Button size="sm" variant="outline" onClick={() => setResolving(null)} className="h-7 text-xs rounded-md">Cancel</Button>
                            {resolveErr && <span className="text-[10px]" style={{ color: C.red }} data-testid="resolve-error">{resolveErr}</span>}
                          </div>
                        </td></tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Controls */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: C.slate }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by reference or business..."
            className="w-full pl-10 pr-4 py-2 text-sm rounded-md border outline-none focus:border-[#2A6FC4]" style={{ borderColor: 'rgba(136,150,168,0.2)' }} data-testid="claim-search" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 text-xs rounded-md border" style={{ borderColor: 'rgba(136,150,168,0.2)' }} data-testid="claim-status-filter">
          <option value="open">Open (Active Book)</option>
          <option value="all">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {['platform_owner', 'mga_admin'].includes(user?.role) && <PaymentRunButton api={api} />}
        <button onClick={load} title="Refreshes automatically every 30 seconds — click to refresh now" data-testid="claims-refresh-btn"
          className="flex items-center gap-1.5 px-2.5 py-2 rounded-md text-[10px] font-medium transition-colors hover:bg-[#F5F7FA]"
          style={{ color: C.slate, border: '1px solid rgba(136,150,168,0.2)' }}>
          <RefreshCw className="w-3 h-3" style={{ color: C.green }} />
          <span className="hidden md:inline">Live · {lastRefresh ? lastRefresh.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}</span>
        </button>
        <Button onClick={() => setShowForm(!showForm)} className="rounded-md text-xs" style={{ background: C.blue }} data-testid="new-fnol-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> New FNOL
        </Button>
      </div>

      {/* FNOL Form */}
      {showForm && (
        <Card className="p-5 rounded-md bg-white" style={{ border: '1px solid rgba(136,150,168,0.15)', borderLeft: `3px solid ${C.teal}` }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: C.navy }}>First Notification of Loss</h3>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label className="text-[11px]" style={{ color: C.slate }}>Policy Number *</Label><Input value={form.policy_number} onChange={e => setForm(p => ({...p, policy_number: e.target.value}))} className="mt-1 rounded-md text-sm" placeholder="e.g. MOT-40431" required data-testid="fnol-policy-number" /></div>
            <div><Label className="text-[11px]" style={{ color: C.slate }}>Business Name</Label><Input value={form.business_name} onChange={e => setForm(p => ({...p, business_name: e.target.value}))} className="mt-1 rounded-md text-sm" required /></div>
            <div><Label className="text-[11px]" style={{ color: C.slate }}>Claimant</Label><Input value={form.claimant_name} onChange={e => setForm(p => ({...p, claimant_name: e.target.value}))} className="mt-1 rounded-md text-sm" /></div>
            <div><Label className="text-[11px]" style={{ color: C.slate }}>Claimant Email</Label><Input type="email" value={form.claimant_email} onChange={e => setForm(p => ({...p, claimant_email: e.target.value}))} className="mt-1 rounded-md text-sm" /></div>
            <div><Label className="text-[11px]" style={{ color: C.slate }}>Claim Type</Label>
              <select value={form.claim_type} onChange={e => setForm(p => ({...p, claim_type: e.target.value}))} className="mt-1 w-full px-3 py-2 border rounded-md text-sm">
                <option value="property">Property</option><option value="liability">Liability</option><option value="motor">Motor</option>
                <option value="business_interruption">Business Interruption</option><option value="theft">Theft</option><option value="cyber">Cyber</option><option value="other">Other</option>
              </select></div>
            <div><Label className="text-[11px]" style={{ color: C.slate }}>Quantum ($)</Label><Input type="number" value={form.estimated_quantum} onChange={e => setForm(p => ({...p, estimated_quantum: e.target.value}))} className="mt-1 rounded-md text-sm" required /></div>
            <div><Label className="text-[11px]" style={{ color: C.slate }}>Date of Loss</Label><Input type="date" value={form.date_of_loss} max={new Date().toISOString().slice(0, 10)} onChange={e => setForm(p => ({...p, date_of_loss: e.target.value}))} className="mt-1 rounded-md text-sm" data-testid="fnol-date-of-loss" /></div>
            <div className="md:col-span-2"><Label className="text-[11px]" style={{ color: C.slate }}>Description</Label>
              <textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} className="mt-1 w-full px-3 py-2 border rounded-md text-sm h-16 resize-none" /></div>
            <div><Label className="text-[11px]" style={{ color: C.slate }}>Location</Label><Input value={form.location_of_loss} onChange={e => setForm(p => ({...p, location_of_loss: e.target.value}))} className="mt-1 rounded-md text-sm" /></div>
            <div className="md:col-span-3 flex items-center gap-2">
              <Button type="submit" disabled={submitting} className="rounded-md text-xs" style={{ background: C.blue }}>{submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Lodge FNOL</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-md text-xs">Cancel</Button>
              {formErr && <span className="text-[11px] font-medium" style={{ color: C.red }} data-testid="fnol-form-error">{formErr}</span>}
            </div>
          </form>
        </Card>
      )}

      {/* Claims Table */}
      <Card className="rounded-md bg-white" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="claims-table">
            <thead><tr className="border-b" style={{ background: '#FAFBFC', borderColor: 'rgba(136,150,168,0.1)' }}>
              {['Reference', 'Business', 'Type', 'Quantum', 'Reserve', 'Paid', 'Pathway', 'Handler', 'Status', ''].map(h =>
                <th key={h} className="text-left py-2.5 px-3 text-[11px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={10} className="p-0">{(search || statusFilter !== 'open') && claims.length === 0 ? <EmptyState type="search" compact /> : claims.length === 0 ? <EmptyState type="claims" /> : <EmptyState type="search" compact />}</td></tr>
              ) : filtered.map(cl => {
                const sc = statusMeta(cl.status);
                const pc = PATHWAY_CONFIG[cl.pathway] || PATHWAY_CONFIG.standard;
                return (
                  <tr key={cl.claim_id} onClick={() => setSelectedClaim(cl)} className="border-b hover:bg-[#FAFBFC] cursor-pointer group" style={{ borderColor: 'rgba(136,150,168,0.06)' }} data-testid={`claim-row-${cl.claim_id}`}>
                    <td className="py-2.5 px-3 font-mono text-xs font-medium" style={{ color: C.blue }}>{cl.claim_reference}</td>
                    <td className="py-2.5 px-3 text-xs font-medium truncate max-w-[160px]" style={{ color: C.navy }}>{cl.business_name}</td>
                    <td className="py-2.5 px-3 text-[11px] capitalize" style={{ color: C.slate }}>{cl.claim_type}</td>
                    <td className="py-2.5 px-3 text-xs font-mono" style={{ color: C.navy }}>${(cl.estimated_quantum||0).toLocaleString()}</td>
                    <td className="py-2.5 px-3 text-xs font-mono" style={{ color: cl.reserve_amount > 0 && !['settled', 'closed', 'declined', 'withdrawn'].includes(cl.status) ? C.amber : C.slate }}>${(cl.reserve_amount||0).toLocaleString()}</td>
                    <td className="py-2.5 px-3 text-xs font-mono" style={{ color: cl.paid_amount > 0 ? C.blue : C.slate }}>${(cl.paid_amount||0).toLocaleString()}</td>
                    <td className="py-2.5 px-3"><span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: pc.color+'12', color: pc.color }}>{pc.label}</span></td>
                    <td className="py-2.5 px-3 text-[11px]" style={{ color: C.slate }}>{cl.handler_name || '—'}</td>
                    <td className="py-2.5 px-3"><span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: sc.color+'15', color: sc.color }}>{sc.label}</span><IdleChip claim={cl} /></td>
                    <td className="py-1 px-2"><ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100" style={{ color: C.blue }} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── ARIA Reserve Advisor ───
function ReserveAdvisor({ claim, api, doAction, processing }) {
  const [rec, setRec] = useState(null);
  const [applied, setApplied] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setRec(null); setApplied(false);
    api.get(`/claims/${claim.claim_id}/reserve-recommendation`).then(r => setRec(r.data)).catch(() => {});
  }, [claim.claim_id, claim.reserve_amount]);

  if (!rec || rec.sample < 5) return null;
  const cfg = {
    under_reserved: { color: C.red, label: 'Under-reserved' },
    over_reserved: { color: C.blue, label: 'Over-reserved' },
    adequate: { color: C.green, label: 'Reserve adequate' },
    no_comparable_history: { color: C.slate, label: 'No comparable history — human judgment applies' },
  }[rec.assessment];

  const apply = async () => {
    await doAction('update-reserve', {
      new_reserve: rec.recommended_reserve,
      reason: `ARIA smart reserve: 75th percentile of ${rec.sample} ${rec.cohort} (median $${Math.round(rec.median_paid).toLocaleString()})`,
    });
    setApplied(true);
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-md flex-wrap" data-testid="reserve-advisor"
      style={{ background: `${cfg.color}08`, border: `1px solid ${cfg.color}30` }}>
      <Sparkles className="w-4 h-4 shrink-0" style={{ color: cfg.color }} />
      <div className="flex-1 min-w-[260px]">
        <p className="text-xs font-semibold" style={{ color: cfg.color }} data-testid="reserve-advisor-assessment">
          ARIA Reserve Advisor: {cfg.label}
        </p>
        <p className="text-[11px] mt-0.5" style={{ color: '#3A4A5A' }}>
          {rec.sample.toLocaleString()} {rec.cohort} settled at a median of ${Math.round(rec.median_paid).toLocaleString()} (P75 ${Math.round(rec.p75_paid).toLocaleString()})
          {rec.development_factor != null && <> · claims of this type typically settle at {Math.round(rec.development_factor * 100)}% of the initial estimate</>}
        </p>
      </div>
      <div className="text-right">
        <p className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>Recommended</p>
        <p className="text-base font-bold" style={{ color: C.navy, fontVariantNumeric: 'tabular-nums' }} data-testid="reserve-advisor-recommended">${Math.round(rec.recommended_reserve).toLocaleString()}</p>
      </div>
      {rec.assessment !== 'adequate' && rec.assessment !== 'no_comparable_history' && !applied && (
        <Button size="sm" disabled={processing} onClick={apply} className="text-xs rounded-md text-white" style={{ background: cfg.color }} data-testid="reserve-advisor-apply">
          Apply ${Math.round(rec.recommended_reserve).toLocaleString()}
        </Button>
      )}
    </div>
  );
}

// ─── Claim Detail View ───
function ClaimDetail({ claim: initialClaim, api, isAdmin, isStaff, onBack, onChanged, user }) {
  const navigate = useNavigate();
  const [claim, setClaim] = useState(initialClaim);
  const [actionForm, setActionForm] = useState(null); // 'info'|'assessor'|'reserve'|'settle'|'decline'|'reopen'|'note'|'recovery'
  const [actionValue, setActionValue] = useState('');
  const [actionValue2, setActionValue2] = useState('');
  const [siuCodes, setSiuCodes] = useState([]);
  const [processing, setProcessing] = useState(false);

  const reload = async () => {
    try { const { data } = await api.get(`/claims/${claim.claim_id}`); setClaim(data); } catch (err) { console.error('API error:', err?.message); }
  };

  const doAction = async (endpoint, body) => {
    setProcessing(true);
    try {
      await api.post(`/claims/${claim.claim_id}/${endpoint}`, body);
      await reload(); setActionForm(null); setActionValue(''); setActionValue2(''); onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Claim action failed');
      console.error('API error:', err?.message);
    }
    setProcessing(false);
  };

  const sc = statusMeta(claim.status);
  const pc = PATHWAY_CONFIG[claim.pathway] || PATHWAY_CONFIG.standard;
  const isOpen = !['settled', 'closed', 'closed_no_payment', 'declined', 'withdrawn'].includes(claim.status);

  return (
    <div className="space-y-4" data-testid="claim-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-md hover:bg-gray-100"><ArrowLeft className="w-4 h-4" style={{ color: C.blue }} /></button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold font-mono" style={{ color: C.navy }}>{claim.claim_reference}</h2>
            <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: sc.color+'15', color: sc.color }}>{sc.label}</span>
            <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: pc.color+'12', color: pc.color }}>{pc.label}</span>
          </div>
          <p className="text-sm" style={{ color: C.slate }}>
            {claim.entity_id
              ? <button onClick={() => navigate(`/client/${claim.entity_id}`)} className="hover:underline" style={{ color: C.blue }} data-testid="view-client-link">{claim.business_name}</button>
              : claim.business_name} — {claim.claim_type}
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1.5 shrink-0">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/claims/${claim.claim_id}/document/acknowledgement`}
              target="_blank" rel="noopener noreferrer" data-testid="claim-ack-pdf-btn"
              className="px-3 py-2 rounded-md text-xs font-semibold flex items-center gap-1.5 hover:opacity-90 transition-opacity"
              style={{ border: '1px solid rgba(26,76,124,0.35)', color: C.blue }} title="Claim acknowledgement letter (PDF)">
              <FileText className="w-3.5 h-3.5" />Ack Letter
            </a>
            {claim.settlement && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/claims/${claim.claim_id}/document/settlement`}
                target="_blank" rel="noopener noreferrer" data-testid="claim-settlement-pdf-btn"
                className="px-3 py-2 rounded-md text-xs font-semibold flex items-center gap-1.5 hover:opacity-90 transition-opacity"
                style={{ border: '1px solid rgba(29,158,117,0.4)', color: C.green }} title="Settlement & discharge document (PDF)">
                <FileText className="w-3.5 h-3.5" />Settlement
              </a>
            )}
            {claim.decline && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/claims/${claim.claim_id}/document/decline-letter`}
                target="_blank" rel="noopener noreferrer" data-testid="claim-decline-letter-btn"
                className="px-3 py-2 rounded-md text-xs font-semibold flex items-center gap-1.5 hover:opacity-90 transition-opacity"
                style={{ border: '1px solid rgba(226,75,74,0.4)', color: C.red }} title="DOC-35 claim decline letter (PDF)">
                <FileText className="w-3.5 h-3.5" />Decline Letter
              </a>
            )}
            {claim.entity_id && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/claims/${claim.claim_id}/document/loss-run`}
                target="_blank" rel="noopener noreferrer" data-testid="claim-lossrun-pdf-btn"
                className="px-3 py-2 rounded-md text-xs font-semibold flex items-center gap-1.5 hover:opacity-90 transition-opacity"
                style={{ border: '1px solid rgba(232,160,32,0.4)', color: '#A8720E' }} title="Client loss run across all policies (PDF)">
                <FileText className="w-3.5 h-3.5" />Loss Run
              </a>
            )}
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/afca-pack/${claim.claim_id}`}
              target="_blank" rel="noopener noreferrer" data-testid="afca-pack-btn"
              className="px-3 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 hover:opacity-90 transition-opacity"
              style={{ background: '#7C5CBF' }} title="Download the ready-to-lodge AFCA response pack (ZIP)">
              <FileText className="w-3.5 h-3.5" />AFCA Pack
            </a>
          </div>
        )}
      </div>

      {/* AI quantum cross-check alert */}
      {claim.quantum_mismatch && (
        <div className="flex items-start gap-2.5 px-4 py-3 rounded-md" data-testid="quantum-mismatch-banner"
          style={{ background: claim.quantum_mismatch.status === 'over_quantum' ? 'rgba(226,75,74,0.07)' : 'rgba(232,160,32,0.08)',
                   border: `1px solid ${claim.quantum_mismatch.status === 'over_quantum' ? 'rgba(226,75,74,0.25)' : 'rgba(232,160,32,0.3)'}` }}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: claim.quantum_mismatch.status === 'over_quantum' ? C.red : C.amber }} />
          <div>
            <p className="text-xs font-semibold" style={{ color: claim.quantum_mismatch.status === 'over_quantum' ? C.red : '#B27516' }}>
              AI Cross-Check: document total does not match claim quantum
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: '#3A4A5A' }}>
              "{claim.quantum_mismatch.document}" — {claim.quantum_mismatch.message}
            </p>
          </div>
        </div>
      )}

      {/* SIU referral panel */}
      {claim.siu_referral && <SIUPanel claim={claim} api={api} onReload={reload} />}

      {/* Settlement discharge */}
      {claim.settlement && <DischargeCard claim={claim} api={api} isAdmin={isAdmin} onReload={reload} />}

      {/* Key metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Quantum" value={`$${(claim.estimated_quantum||0).toLocaleString()}`} color={C.navy} />
        <KPI label="Reserve" value={`$${(claim.reserve_amount||0).toLocaleString()}`} color={['settled', 'closed', 'declined'].includes(claim.status) ? C.slate : C.amber} />
        <KPI label="Paid" value={`$${(claim.paid_amount||0).toLocaleString()}`} color={C.green} />
        <KPI label="Handler" value={claim.handler_name || 'Unassigned'} color={C.blue} />
        <KPI label="Authority" value={claim.authority?.level || '—'} color={C.slate} />
      </div>
      {claim.quantum_stated && (
        <p className="text-[10px] -mt-1" style={{ color: C.slate }} data-testid="claim-quantum-stated">
          Quantum stated by {claim.quantum_stated.by} on {(claim.quantum_stated.at || '').slice(0, 10)} · {claim.quantum_stated.source_label} — {claim.quantum_stated.basis}
        </p>
      )}

      {/* ARIA smart reserve recommendation */}
      {isAdmin && isOpen && <ReserveAdvisor claim={claim} api={api} doAction={doAction} processing={processing} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Details + Actions */}
        <div className="lg:col-span-2 space-y-4">
          {/* Claim Details */}
          <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Claim Details</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Detail label="Policy" value={claim.policy_number || '—'} />
              <Detail label="Claimant" value={claim.claimant_name || claim.business_name} />
              <Detail label="Date of Loss" value={
                <span className="inline-flex items-center gap-1.5">
                  {claim.date_of_loss ? new Date(claim.date_of_loss).toLocaleDateString('en-AU') : '—'}
                  {claim.date_of_loss_confirmed
                    ? <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ background: 'rgba(29,158,117,0.12)', color: C.green }} data-testid="dol-confirmed-badge">CONFIRMED</span>
                    : <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ background: 'rgba(232,160,32,0.15)', color: '#B27516' }} data-testid="dol-unconfirmed-badge">UNCONFIRMED</span>}
                </span>
              } />
              <Detail label="Reported" value={new Date(claim.date_reported || claim.created_at).toLocaleDateString('en-AU')} />
              <Detail label="Location" value={claim.location_of_loss || '—'} />
              <Detail label="Excess" value={`$${(claim.excess||0).toLocaleString()}`} />
              <Detail label="Reporter" value={claim.reported_by_name || '—'} />
              <Detail label="Assessor" value={claim.assessor?.name || 'None appointed'} />
            </div>
            {claim.description && <p className="text-xs mt-3 p-3 rounded" style={{ background: C.bg, color: C.navy }}>{claim.description}</p>}
            {claim.decline_reason && <p className="text-xs mt-3 p-3 rounded" style={{ background: 'rgba(226,75,74,0.05)', color: C.red }} data-testid="decline-reason-display">
              {claim.decline ? `Declined — provisions relied on: ${claim.decline_reason}` : `Decline reason: ${claim.decline_reason}`}
              {claim.decline && <span className="block mt-1 text-[10px]" style={{ color: C.slate }}>Full clause text, evidence and reasoning are on the DOC-35 decline letter.</span>}
            </p>}
          </Card>

          {/* Reinsurance recovery (admin) */}
          {isStaff && <ClaimReinsurancePanel claimId={claim.claim_id} />}

          {/* Documents */}
          <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.navy }}>Documents</h3>
            <RecordDocuments recordType="claim" recordId={claim.claim_id} />
          </Card>

          {/* Actions */}
          {isAdmin && isOpen && (
            <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Actions</h3>
              <div className="flex flex-wrap gap-2">
                {!claim.date_of_loss_confirmed && <ActionBtn label="Confirm Date of Loss" icon={CheckCircle} color={C.teal} onClick={() => setActionForm('confirm-dol')} />}
                <ActionBtn label="Request Info" icon={Send} color={C.amber} onClick={() => setActionForm('info')} />
                <ActionBtn label="Appoint Assessor" icon={Users} color={C.blue} onClick={() => setActionForm('assessor')} />
                <ActionBtn label="Update Reserve" icon={RefreshCw} color={C.amber} onClick={() => setActionForm('reserve')} />
                <ActionBtn label="Approve Settlement" icon={CheckCircle} color={C.green} onClick={() => setActionForm('settle')} />
                <ActionBtn label="Decline" icon={XCircle} color={C.red} onClick={() => setActionForm('decline')} />
                <ActionBtn label="Add Note" icon={MessageSquare} color={C.slate} onClick={() => setActionForm('note')} />
                <ActionBtn label="Recovery" icon={Shield} color={C.blue} onClick={() => setActionForm('recovery')} />
                {(!claim.siu_referral || !['open', 'investigating'].includes(claim.siu_referral.status)) &&
                  <ActionBtn label="Refer to SIU" icon={AlertTriangle} color={'#7C2D12'} onClick={() => { setSiuCodes([]); setActionForm('siu'); }} />}
              </div>
              {actionForm && (
                <div className="mt-3 p-3 rounded-md" style={{ background: C.bg }}>
                  {actionForm === 'confirm-dol' && (<>
                    <p className="text-xs font-medium mb-2" style={{ color: C.navy }}>Confirm Date of Loss (leave blank to confirm the date on file: {claim.date_of_loss ? new Date(claim.date_of_loss).toLocaleDateString('en-AU') : '—'})</p>
                    <Input type="date" value={actionValue} max={new Date().toISOString().slice(0, 10)} onChange={e => setActionValue(e.target.value)} className="text-sm rounded-md max-w-[200px]" data-testid="confirm-dol-input" />
                    <div className="flex gap-2 mt-2"><Button size="sm" disabled={processing} onClick={() => doAction('confirm-dol', { date_of_loss: actionValue || null })} className="text-xs rounded-md" style={{ background: C.teal }} data-testid="confirm-dol-submit">Confirm DOL</Button><Button size="sm" variant="outline" onClick={() => setActionForm(null)} className="text-xs rounded-md">Cancel</Button></div>
                  </>)}
                  {actionForm === 'info' && (<>
                    <p className="text-xs font-medium mb-2" style={{ color: C.navy }}>Request Information</p>
                    <textarea value={actionValue} onChange={e => setActionValue(e.target.value)} placeholder="What information is needed from the broker?" className="w-full px-3 py-2 border rounded-md text-sm h-16 resize-none" />
                    <div className="flex gap-2 mt-2"><Button size="sm" disabled={processing} onClick={() => doAction('request-info', { info_requested: actionValue })} className="text-xs rounded-md" style={{ background: C.amber }}>Send Request</Button><Button size="sm" variant="outline" onClick={() => setActionForm(null)} className="text-xs rounded-md">Cancel</Button></div>
                  </>)}
                  {actionForm === 'assessor' && (<>
                    <p className="text-xs font-medium mb-2" style={{ color: C.navy }}>Appoint Assessor</p>
                    <div className="grid grid-cols-2 gap-2"><Input value={actionValue} onChange={e => setActionValue(e.target.value)} placeholder="Assessor name" className="text-sm rounded-md" />
                    <select value={actionValue2} onChange={e => setActionValue2(e.target.value)} className="px-3 py-2 border rounded-md text-sm"><option value="loss_adjuster">Loss Adjuster</option><option value="investigator">Investigator</option><option value="engineer">Engineer</option><option value="forensic">Forensic</option></select></div>
                    <div className="flex gap-2 mt-2"><Button size="sm" disabled={processing} onClick={() => doAction('appoint-assessor', { assessor_name: actionValue, assessor_type: actionValue2 || 'loss_adjuster' })} className="text-xs rounded-md" style={{ background: C.blue }}>Appoint</Button><Button size="sm" variant="outline" onClick={() => setActionForm(null)} className="text-xs rounded-md">Cancel</Button></div>
                  </>)}
                  {actionForm === 'reserve' && (<>
                    <p className="text-xs font-medium mb-2" style={{ color: C.navy }}>Update Reserve (current: ${(claim.reserve_amount||0).toLocaleString()})</p>
                    <div className="grid grid-cols-2 gap-2"><Input type="number" value={actionValue} onChange={e => setActionValue(e.target.value)} placeholder="New reserve $" className="text-sm rounded-md" /><Input value={actionValue2} onChange={e => setActionValue2(e.target.value)} placeholder="Reason" className="text-sm rounded-md" /></div>
                    <div className="flex gap-2 mt-2"><Button size="sm" disabled={processing} onClick={() => doAction('update-reserve', { new_reserve: parseFloat(actionValue), reason: actionValue2 })} className="text-xs rounded-md" style={{ background: C.amber }}>Update</Button><Button size="sm" variant="outline" onClick={() => setActionForm(null)} className="text-xs rounded-md">Cancel</Button></div>
                  </>)}
                  {actionForm === 'settle' && (<>
                    <p className="text-xs font-medium mb-2" style={{ color: C.navy }}>Approve Settlement</p>
                    {!claim.date_of_loss_confirmed && <p className="text-[11px] mb-2 px-2 py-1.5 rounded" style={{ background: 'rgba(232,160,32,0.12)', color: '#B27516' }} data-testid="settle-dol-warning">Date of loss must be confirmed before settlement can be approved.</p>}
                    <p className="text-[11px] mb-2" style={{ color: C.slate }}>Policy excess of ${(claim.excess || 0).toLocaleString()} will be deducted from the gross amount — net payable{actionValue ? `: $${Math.max(parseFloat(actionValue) - (claim.excess || 0), 0).toLocaleString()}` : ' shown on entry'}.</p>
                    <div className="grid grid-cols-2 gap-2"><Input type="number" value={actionValue} onChange={e => setActionValue(e.target.value)} placeholder="Settlement amount $" className="text-sm rounded-md" /><Input value={actionValue2} onChange={e => setActionValue2(e.target.value)} placeholder="Notes" className="text-sm rounded-md" /></div>
                    <div className="flex gap-2 mt-2"><Button size="sm" disabled={processing} onClick={() => doAction('approve-settlement', { amount: parseFloat(actionValue), notes: actionValue2 })} className="text-xs rounded-md" style={{ background: C.green }}>Approve</Button><Button size="sm" variant="outline" onClick={() => setActionForm(null)} className="text-xs rounded-md">Cancel</Button></div>
                  </>)}
                  {actionForm === 'decline' && (
                    <StructuredDeclineForm api={api} claim={claim}
                      onDone={async () => { await reload(); setActionForm(null); onChanged && onChanged(); }}
                      onCancel={() => setActionForm(null)} />
                  )}
                  {actionForm === 'note' && (<>
                    <p className="text-xs font-medium mb-2" style={{ color: C.navy }}>Add Note</p>
                    <textarea value={actionValue} onChange={e => setActionValue(e.target.value)} placeholder="Note text" className="w-full px-3 py-2 border rounded-md text-sm h-16 resize-none" />
                    <div className="flex gap-2 mt-2"><Button size="sm" disabled={processing} onClick={() => doAction('note', { text: actionValue, note_type: 'note' })} className="text-xs rounded-md" style={{ background: C.slate }}>Add Note</Button><Button size="sm" variant="outline" onClick={() => setActionForm(null)} className="text-xs rounded-md">Cancel</Button></div>
                  </>)}
                  {actionForm === 'siu' && (<>
                    <p className="text-xs font-medium mb-2" style={{ color: '#7C2D12' }}>Refer to Special Investigations Unit — select reason code(s)</p>
                    {(claim.triage?.fraud_signals || 0) > 0 && (
                      <p className="text-[11px] mb-2 px-2 py-1.5 rounded" style={{ background: 'rgba(226,75,74,0.08)', color: C.red }} data-testid="siu-fraud-signal-hint">
                        ARIA triage flagged {claim.triage.fraud_signals} fraud signal(s) on this claim.
                      </p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mb-2" data-testid="siu-reason-grid">
                      {SIU_REASONS.map(r => (
                        <label key={r.code} className="flex items-center gap-2 text-[11px] cursor-pointer py-0.5" style={{ color: C.navy }}>
                          <input type="checkbox" checked={siuCodes.includes(r.code)} data-testid={`siu-reason-${r.code}`}
                            onChange={() => setSiuCodes(cs => cs.includes(r.code) ? cs.filter(x => x !== r.code) : [...cs, r.code])} />
                          {r.label}
                        </label>
                      ))}
                    </div>
                    <textarea value={actionValue} onChange={e => setActionValue(e.target.value)} placeholder="Referral notes for the investigator (optional)" className="w-full px-3 py-2 border rounded-md text-sm h-14 resize-none" data-testid="siu-notes-input" />
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" disabled={processing || siuCodes.length === 0} onClick={() => doAction('siu/refer', { reason_codes: siuCodes, notes: actionValue })} className="text-xs rounded-md" style={{ background: '#7C2D12' }} data-testid="siu-refer-submit">Refer to SIU</Button>
                      <Button size="sm" variant="outline" onClick={() => setActionForm(null)} className="text-xs rounded-md">Cancel</Button>
                    </div>
                  </>)}
                  {actionForm === 'recovery' && (<>
                    <p className="text-xs font-medium mb-2" style={{ color: C.navy }}>Initiate Recovery / Subrogation</p>
                    <div className="grid grid-cols-2 gap-2"><Input type="number" value={actionValue} onChange={e => setActionValue(e.target.value)} placeholder="Recovery amount $" className="text-sm rounded-md" /><Input value={actionValue2} onChange={e => setActionValue2(e.target.value)} placeholder="Recover from (party name)" className="text-sm rounded-md" /></div>
                    <div className="flex gap-2 mt-2"><Button size="sm" disabled={processing} onClick={() => doAction('recovery', { recovery_amount: parseFloat(actionValue), recovery_from: actionValue2 })} className="text-xs rounded-md" style={{ background: C.blue }}>Initiate</Button><Button size="sm" variant="outline" onClick={() => setActionForm(null)} className="text-xs rounded-md">Cancel</Button></div>
                  </>)}
                </div>
              )}
            </Card>
          )}

          {/* Reopen for closed/declined */}
          {isAdmin && !isOpen && claim.status !== 'settled' && actionForm !== 'reopen' && (
            <Button size="sm" onClick={() => { setActionForm('reopen'); setActionValue('Reopened for review'); setActionValue2('1'); }} className="text-xs rounded-md" style={{ background: C.amber }} data-testid="reopen-claim-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reopen Claim
            </Button>
          )}
          {isAdmin && !isOpen && actionForm === 'reopen' && (() => {
            const lastReserve = claim.reserve_before_close || [...(claim.reserve_history || [])].reverse().find(r => (r.amount || 0) > 0)?.amount || 0;
            return (
              <Card className="p-3 rounded-md mt-2" style={{ border: '1px solid rgba(232,160,32,0.4)' }} data-testid="reopen-form">
                <p className="text-xs font-semibold mb-2" style={{ color: C.navy }}>Reopen claim</p>
                <input value={actionValue} onChange={e => setActionValue(e.target.value)} placeholder="Reason for reopening..."
                  data-testid="reopen-reason-input"
                  className="w-full px-2.5 py-1.5 text-xs rounded-sm border focus:outline-none mb-2"
                  style={{ borderColor: 'rgba(136,150,168,0.35)', color: C.navy }} />
                {lastReserve > 0 && (
                  <label className="flex items-center gap-2 text-xs mb-2 cursor-pointer" style={{ color: C.navy }} data-testid="restore-reserve-label">
                    <input type="checkbox" checked={actionValue2 === '1'} onChange={e => setActionValue2(e.target.checked ? '1' : '')}
                      data-testid="restore-reserve-checkbox" />
                    Restore last reserve of <b style={{ color: C.amber }}>${lastReserve.toLocaleString()}</b> so the claim isn't sitting at $0
                  </label>
                )}
                <div className="flex gap-2">
                  <Button size="sm" disabled={processing || !actionValue.trim()} data-testid="reopen-confirm-btn"
                    onClick={() => doAction('reopen', { reason: actionValue, restore_reserve: actionValue2 === '1' })}
                    className="text-xs rounded-md" style={{ background: C.amber }}>
                    {processing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Confirm Reopen
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setActionForm(null); setActionValue(''); setActionValue2(''); }} className="text-xs rounded-md">Cancel</Button>
                </div>
              </Card>
            );
          })()}
        </div>

        {/* Right: Diary + Timeline */}
        <div>
          <ClaimDiaryCard claim={claim} api={api} isAdmin={isAdmin} isOpen={isOpen} onReload={reload} />

          <PartFPromisesCard api={api} claimId={claim.claim_id} />
          <ClaimTimeline claim={claim} canExport={isAdmin} />

          {/* Reserve History */}
          {claim.reserve_history?.length > 1 && (
            <Card className="p-4 rounded-md mt-3" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Reserve History</h3>
              {claim.reserve_history.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                  <div><p className="text-xs" style={{ color: C.navy }}>{r.reason}</p><p className="text-[10px]" style={{ color: C.slate }}>{r.by} — {new Date(r.date).toLocaleDateString('en-AU')}</p></div>
                  <span className="text-xs font-mono font-semibold" style={{ color: C.navy }}>${r.amount?.toLocaleString()}</span>
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function DischargeCard({ claim, api, isAdmin, onReload }) {
  const [busy, setBusy] = useState(false);
  const st = claim.settlement;
  if (!st) return null;
  const d = claim.discharge || { status: 'awaiting_return' };
  const returned = d.status === 'returned';
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const markReturned = async () => {
    setBusy(true);
    try { await api.post(`/claims/${claim.claim_id}/discharge/mark-returned`); await onReload(); }
    catch (err) { console.error('API error:', err?.message); }
    setBusy(false);
  };
  return (
    <Card className="p-4 rounded-md" style={{ background: returned ? 'rgba(29,158,117,0.04)' : 'rgba(232,160,32,0.05)', border: `1px solid ${returned ? 'rgba(29,158,117,0.3)' : 'rgba(232,160,32,0.35)'}` }} data-testid="discharge-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <FileText className="w-4 h-4" style={{ color: returned ? C.green : C.amber }} />
          <span className="text-sm font-semibold" style={{ color: C.navy }}>Settlement Discharge</span>
          <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: (returned ? C.green : C.amber) + '15', color: returned ? C.green : '#B27516' }} data-testid="discharge-status">
            {returned ? 'Signed & Returned' : 'Awaiting Signed Return'}
          </span>
          <span className="text-[11px]" style={{ color: C.slate }}>
            Net ${(st.amount || 0).toLocaleString()} (gross ${(st.gross_amount || 0).toLocaleString()} less excess ${(st.excess_deducted || 0).toLocaleString()})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${BACKEND}/api/documents/discharge-receipt/${claim.claim_id}`} target="_blank" rel="noopener noreferrer" data-testid="discharge-pdf-btn"
            className="px-2.5 py-1 rounded-md text-[10px] font-semibold inline-flex items-center gap-1"
            style={{ border: '1px solid rgba(26,76,124,0.3)', color: C.blue }}>
            <FileText className="w-3 h-3" />Discharge Receipt PDF
          </a>
          {isAdmin && !returned && (
            <button onClick={markReturned} disabled={busy} data-testid="discharge-mark-returned-btn"
              className="px-2.5 py-1 rounded-md text-[10px] font-semibold text-white disabled:opacity-40" style={{ background: C.green }}>
              {busy ? '...' : 'Mark Returned'}
            </button>
          )}
        </div>
      </div>
      {returned && d.returned_at && (
        <p className="text-[11px] mt-1.5" style={{ color: C.slate }}>Returned {new Date(d.returned_at).toLocaleDateString('en-AU')} · recorded by {d.recorded_by}</p>
      )}
    </Card>
  );
}

function SIUPanel({ claim, api, onReload }) {
  const ref = claim.siu_referral;
  const [closing, setClosing] = useState(null); // 'cleared'|'substantiated'|'withdrawn'
  const [outcome, setOutcome] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!ref) return null;
  const sm = SIU_STATUS_META[ref.status] || SIU_STATUS_META.open;
  const active = ['open', 'investigating'].includes(ref.status);
  const put = async (status, notes = '') => {
    setBusy(true); setErr('');
    try { await api.put(`/claims/siu/${ref.referral_id}/status`, { status, outcome_notes: notes }); await onReload(); setClosing(null); setOutcome(''); }
    catch (e) { setErr(e?.response?.data?.detail || 'Update failed'); }
    setBusy(false);
  };
  return (
    <Card className="p-4 rounded-md" style={{ background: 'rgba(124,45,18,0.04)', border: '1px solid rgba(124,45,18,0.3)' }} data-testid="siu-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Shield className="w-4 h-4" style={{ color: '#7C2D12' }} />
          <span className="text-sm font-semibold" style={{ color: '#7C2D12' }}>SIU Referral</span>
          <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: sm.color + '15', color: sm.color }} data-testid="siu-panel-status">{sm.label}</span>
          <span className="text-[11px]" style={{ color: C.slate }}>referred by {ref.referred_by} on {new Date(ref.created_at).toLocaleDateString('en-AU')}</span>
        </div>
        {active && (
          <div className="flex gap-1.5 flex-wrap">
            {ref.status === 'open' && (
              <button onClick={() => put('investigating')} disabled={busy} data-testid="siu-investigate-btn"
                className="px-2.5 py-1 rounded-md text-[10px] font-semibold text-white disabled:opacity-40" style={{ background: C.amber }}>Start Investigation</button>
            )}
            <button onClick={() => setClosing(closing === 'cleared' ? null : 'cleared')} disabled={busy} data-testid="siu-clear-btn"
              className="px-2.5 py-1 rounded-md text-[10px] font-semibold text-white disabled:opacity-40" style={{ background: C.green }}>Clear</button>
            <button onClick={() => setClosing(closing === 'substantiated' ? null : 'substantiated')} disabled={busy} data-testid="siu-substantiate-btn"
              className="px-2.5 py-1 rounded-md text-[10px] font-semibold text-white disabled:opacity-40" style={{ background: '#7C2D12' }}>Substantiate</button>
            <button onClick={() => setClosing(closing === 'withdrawn' ? null : 'withdrawn')} disabled={busy} data-testid="siu-withdraw-btn"
              className="px-2.5 py-1 rounded-md text-[10px] font-semibold disabled:opacity-40" style={{ background: 'rgba(92,107,122,0.12)', color: C.slate }}>Withdraw</button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        {(ref.reason_codes || []).map(c => (
          <span key={c} className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: 'rgba(124,45,18,0.08)', color: '#7C2D12' }} data-testid={`siu-code-${c}`}>
            {SIU_REASONS.find(r => r.code === c)?.label || c}
          </span>
        ))}
      </div>
      {closing && (
        <div className="mt-2 space-y-2" data-testid="siu-outcome-form">
          <textarea value={outcome} onChange={e => setOutcome(e.target.value)} rows={2}
            placeholder={`Outcome notes (required to mark ${closing})...`}
            className="w-full px-3 py-2 text-xs rounded-md border outline-none" style={{ borderColor: 'rgba(124,45,18,0.25)' }} data-testid="siu-outcome-input" />
          <div className="flex gap-2">
            <button onClick={() => put(closing, outcome)} disabled={busy || !outcome.trim()} data-testid="siu-outcome-confirm"
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-white disabled:opacity-40"
              style={{ background: closing === 'cleared' ? C.green : closing === 'substantiated' ? '#7C2D12' : C.slate }}>
              Confirm {closing}
            </button>
            <button onClick={() => setClosing(null)} className="px-3 py-1.5 rounded-md text-xs" style={{ color: C.slate }}>Cancel</button>
          </div>
        </div>
      )}
      {err && <p className="text-[11px] mt-2" style={{ color: C.red }} data-testid="siu-error">{err}</p>}
    </Card>
  );
}

function ClaimDiaryCard({ claim, api, isAdmin, isOpen, onReload }) {
  const [busy, setBusy] = useState(null);
  const diary = claim.diary || [];
  if (!diary.length) return null;
  const isOverdue = (d) => d.status !== 'completed' && new Date(d.due_date) <= new Date();
  const overdueCount = diary.filter(isOverdue).length;
  const complete = async (day) => {
    setBusy(day);
    try { await api.post(`/claims/${claim.claim_id}/diary/${day}/complete`); await onReload(); }
    catch (err) { console.error('API error:', err?.message); }
    setBusy(null);
  };
  const dotColor = (d) => d.status === 'completed' ? C.green : isOverdue(d) ? C.red : C.slate;
  return (
    <Card className="p-4 rounded-md" style={{ border: `1px solid ${overdueCount ? 'rgba(226,75,74,0.3)' : 'rgba(136,150,168,0.12)'}` }} data-testid="claim-diary-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Claims Diary — 8-Point Schedule</h3>
        {overdueCount > 0
          ? <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: 'rgba(226,75,74,0.1)', color: C.red }} data-testid="diary-overdue-badge">{overdueCount} AWAITING C-SUITE</span>
          : <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: 'rgba(0,180,216,0.1)', color: '#0E7490' }} data-testid="diary-aria-badge">ARIA HANDLING</span>}
      </div>
      <div className="space-y-0">
        {diary.map((d) => {
          const auto = d.auto || d.completed_by === 'ARIA-CLM (auto)';
          return (
          <div key={d.day} className="flex items-start gap-2.5 pb-2.5 mb-2.5 border-b" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`diary-point-${d.day}`}>
            <div className="w-2 h-2 rounded-full mt-1 shrink-0" style={{ background: dotColor(d) }} />
            <div className="flex-1 min-w-0">
              <p className="text-xs" style={{ color: C.navy, textDecoration: d.status === 'completed' ? 'line-through' : 'none', opacity: d.status === 'completed' ? 0.6 : 1 }}>
                <span className="font-semibold">Day {d.day}</span> — {d.action}
              </p>
              <p className="text-[10px] mt-0.5" style={{ color: C.slate }}>
                Due {new Date(d.due_date).toLocaleDateString('en-AU')}
                {isOverdue(d) && <span className="ml-1.5 px-1 py-0.5 rounded text-[9px] font-bold" style={{ background: 'rgba(226,75,74,0.1)', color: C.red }}>NEEDS DECISION → {d.escalation}</span>}
                {d.status === 'completed' && auto && <span className="ml-1 px-1 py-0.5 rounded text-[9px] font-semibold" style={{ background: 'rgba(0,180,216,0.1)', color: '#0E7490' }}>✓ ARIA (auto)</span>}
                {d.status === 'completed' && !auto && d.completed_by && <span className="ml-1" style={{ color: C.green }}>✓ {d.completed_by}</span>}
              </p>
            </div>
            {isAdmin && isOpen && d.status !== 'completed' && (
              <button onClick={() => complete(d.day)} disabled={busy === d.day} data-testid={`diary-complete-${d.day}`}
                className="px-2 py-0.5 rounded text-[10px] font-semibold shrink-0 disabled:opacity-40"
                style={{ background: 'rgba(29,158,117,0.1)', color: C.green }}>
                {busy === d.day ? '...' : 'Done'}
              </button>
            )}
          </div>
          );
        })}
      </div>
    </Card>
  );
}

function KPI({ label, value, color }) {
  return (<Card className="px-4 py-3 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
    <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</p>
    <p className="text-lg font-semibold mt-0.5 font-mono" style={{ color }}>{value}</p>
  </Card>);
}
function Detail({ label, value }) {
  return (<div className="flex justify-between py-1 border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
    <span className="text-[11px]" style={{ color: C.slate }}>{label}</span><span className="text-xs font-medium" style={{ color: C.navy }}>{value}</span>
  </div>);
}
function ActionBtn({ label, icon: Icon, color, onClick }) {
  return (<button onClick={onClick} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors hover:opacity-80" style={{ background: color+'12', color }}>
    <Icon className="w-3.5 h-3.5" />{label}
  </button>);
}
